import { useState, type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { ErrorBoundary } from './components/error-boundary';
import { ToastProvider } from './components/toast';
import { ConsoleSessionProvider } from './lib/session';
import { createQueryClient } from './lib/query';
import { AppRoutes } from './routes';
import { AuthGate } from './shells/auth-gate';
import { SessionGate } from './shells/session-gate';

/**
 * Provider stack, in dependency order:
 *   auth gate → query cache → toasts → session (needs a signed-in token
 *   provider, since its first act is `GET /me`) → screens.
 *
 * Exported separately from `App` so tests can mount the same stack around a
 * `MemoryRouter` and a seeded `QueryClient`.
 */
export function AppProviders({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) {
  return (
    <AuthGate>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ConsoleSessionProvider>{children}</ConsoleSessionProvider>
        </ToastProvider>
      </QueryClientProvider>
    </AuthGate>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);

  return (
    <BrowserRouter>
      <AppProviders queryClient={queryClient}>
        <ErrorBoundary>
          {/*
            Here rather than inside the `AppProviders` component body, on purpose.
            It has to sit *within* the rendered provider tree — `useSession` needs
            `ConsoleSessionProvider` — but not as part of the stack `AppProviders`
            itself renders, because `renderWithProviders` wraps that stack around
            each page under test. Putting the gate in there would send all 172
            page tests to the dead end. See the note in `session-gate.tsx`.
          */}
          <SessionGate>
            <AppRoutes />
          </SessionGate>
        </ErrorBoundary>
      </AppProviders>
    </BrowserRouter>
  );
}

export default App;
